import React, { useState, useEffect } from 'react';
import AppLayout from '../components/AppLayout';
import { 
  TrendingUp, DollarSign, Users, Calendar, Target, TrendingDown, Archive,
  Activity, Clock, CheckCircle, Send, Eye, UserPlus, PoundSterling,
  CheckSquare, AlertTriangle, Package, Building2, XCircle
} from 'lucide-react';
import { 
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, 
  Legend, ResponsiveContainer, PieChart, Pie, Cell 
} from 'recharts';
import { useTheme } from '../contexts/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Dashboard = () => {
  const { isDark } = useTheme();
  
  // Existing state
  const [metrics, setMetrics] = useState({
    arr: 0,
    mrr: 0,
    active_contracts: 0,
    next_month_renewals: 0
  });
  const [pipeline, setPipeline] = useState({
    pipeline_total: 0,
    forecast_total: 0,
    won_revenue: 0,
    proposals_count: 0,
    signed_count: 0,
    proposals_in_pipeline: []
  });
  const [revenueTimeline, setRevenueTimeline] = useState({
    months: [],
    revenue: [],
    renewals: []
  });
  const [retirementStats, setRetirementStats] = useState({
    period_days: 90,
    total_retired: 0,
    data: []
  });
  const [retirementDays, setRetirementDays] = useState(90);
  
  // New state for enhanced features
  const [activityFeed, setActivityFeed] = useState([]);
  const [pipelineVelocity, setPipelineVelocity] = useState({
    avg_days_to_close: 0,
    win_rate: 0,
    stuck_deals_count: 0,
    value_at_risk: 0,
    avg_pipeline_age: 0
  });
  const [industries, setIndustries] = useState([]);
  const [products, setProducts] = useState([]);
  const [lossReasons, setLossReasons] = useState([]);
  const [analyticsPeriod, setAnalyticsPeriod] = useState('month');
  
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  useEffect(() => {
    fetchRetirementStats();
  }, [retirementDays]);

  useEffect(() => {
    fetchAnalyticsData();
  }, [analyticsPeriod]);

  const fetchDashboardData = async () => {
    try {
      const [metricsRes, timelineRes, pipelineRes, retirementRes, activityRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/dashboard/metrics`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/dashboard/revenue-timeline`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/dashboard/pipeline`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/dashboard/retirement-stats?days=90`, { credentials: 'include' }),
        fetch(`${BACKEND_URL}/api/intelligence/activity-feed?limit=15`, { credentials: 'include' })
      ]);

      if (metricsRes.ok) setMetrics(await metricsRes.json());
      if (timelineRes.ok) setRevenueTimeline(await timelineRes.json());
      if (pipelineRes.ok) setPipeline(await pipelineRes.json());
      if (retirementRes.ok) setRetirementStats(await retirementRes.json());
      if (activityRes.ok) setActivityFeed(await activityRes.json());
      
      // Fetch analytics data
      await fetchAnalyticsData();
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAnalyticsData = async () => {
    // Helper function to safely fetch and parse JSON
    const safeFetch = async (url, fallback = null) => {
      try {
        const res = await fetch(url, { credentials: 'include' });
        if (res.ok) {
          return await res.json();
        }
        console.warn(`API returned ${res.status} for ${url}`);
        return fallback;
      } catch (err) {
        console.error(`Failed to fetch ${url}:`, err);
        return fallback;
      }
    };

    try {
      const [velocityData, industriesData, productsData, lossData] = await Promise.all([
        safeFetch(`${BACKEND_URL}/api/intelligence/pipeline-velocity?period=${analyticsPeriod}`, null),
        safeFetch(`${BACKEND_URL}/api/intelligence/industries?period=${analyticsPeriod}`, []),
        safeFetch(`${BACKEND_URL}/api/intelligence/products?period=${analyticsPeriod}`, []),
        safeFetch(`${BACKEND_URL}/api/intelligence/loss-reasons?period=${analyticsPeriod}`, [])
      ]);

      if (velocityData) setPipelineVelocity(velocityData);
      if (industriesData) setIndustries(industriesData);
      if (productsData) setProducts(productsData);
      if (lossData) setLossReasons(lossData);
    } catch (error) {
      console.error('Failed to fetch analytics data:', error);
    }
  };

  const fetchRetirementStats = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/dashboard/retirement-stats?days=${retirementDays}`, { credentials: 'include' });
      if (response.ok) {
        setRetirementStats(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch retirement stats:', error);
    }
  };

  const chartData = revenueTimeline.months?.map((month, index) => ({
    month,
    Revenue: revenueTimeline.revenue?.[index] || 0,
    Renewals: revenueTimeline.renewals?.[index] || 0
  })) || [];

  const pipelineBreakdownData = [
    { name: 'Monthly (as ARR)', value: pipeline.pipeline_mrr * 12, count: pipeline.monthly_contracts_count },
    { name: 'Annual', value: pipeline.pipeline_arr - (pipeline.pipeline_mrr * 12), count: pipeline.annual_contracts_count }
  ].filter(item => item.value > 0);

  const COLORS = ['#A2FF00', '#00D9FF', '#8B5CF6', '#F59E0B', '#EC4899', '#10B981'];

  // Activity icon mapping
  const getActivityIcon = (type) => {
    const icons = {
      'proposal_signed': <CheckCircle className="w-4 h-4 text-green-400" />,
      'proposal_sent': <Send className="w-4 h-4 text-blue-400" />,
      'proposal_viewed': <Eye className="w-4 h-4 text-cyan-400" />,
      'task_completed': <CheckSquare className="w-4 h-4 text-purple-400" />,
      'lead_created': <UserPlus className="w-4 h-4 text-yellow-400" />,
      'payment_received': <PoundSterling className="w-4 h-4 text-green-400" />
    };
    return icons[type] || <Activity className="w-4 h-4 text-gray-400" />;
  };

  if (loading) {
    return (
      <AppLayout title="Dashboard">
        <div className="flex items-center justify-center h-64">
          <div className="text-[#A2FF00] text-xl">Loading...</div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout title="Dashboard">
      <div className="space-y-8">
        {/* Top Section: Forecast Pipeline + Activity Feed */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Forecast Pipeline Highlight Box */}
          <div className="lg:col-span-2 bg-gradient-to-r from-[#00D9FF]/10 to-[#A2FF00]/10 border-2 border-[#00D9FF] rounded-sm p-6" data-testid="forecast-pipeline-highlight">
            <div className="flex items-center gap-3 mb-4">
              <TrendingDown className="w-8 h-8 text-[#00D9FF]" />
              <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} style={{ fontFamily: 'Unbounded, sans-serif' }}>
                Forecast Pipeline
              </h2>
              <span className="text-xs bg-[#00D9FF]/20 text-[#00D9FF] px-2 py-1 rounded font-mono">
                WEIGHTED BY DEAL PROBABILITY
              </span>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className={`${isDark ? 'bg-[#0A0A0A]/60' : 'bg-white/60'} rounded-sm p-4`}>
                <div className="text-sm text-gray-400 mb-1">Forecast Value</div>
                <div className="text-3xl font-bold text-[#00D9FF]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  £{(pipeline.forecast_total || 0).toLocaleString()}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  Weighted by probability %
                </div>
              </div>
              
              <div className={`${isDark ? 'bg-[#0A0A0A]/60' : 'bg-white/60'} rounded-sm p-4`}>
                <div className="text-sm text-gray-400 mb-1">Won Revenue</div>
                <div className={`text-3xl font-bold ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`} style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  £{(pipeline.won_revenue || 0).toLocaleString()}
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  {pipeline.signed_count || 0} signed proposal{pipeline.signed_count !== 1 ? 's' : ''}
                </div>
              </div>
              
              <div className={`${isDark ? 'bg-[#0A0A0A]/60' : 'bg-white/60'} rounded-sm p-4`}>
                <div className="text-sm text-gray-400 mb-1">Forecast vs Total</div>
                <div className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  {pipeline.pipeline_total > 0 ? Math.round((pipeline.forecast_total / pipeline.pipeline_total) * 100) : 0}%
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  of total pipeline value
                </div>
              </div>
            </div>
          </div>

          {/* Activity Feed Widget */}
          <div className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-white border-gray-200'} border rounded-sm p-4`} data-testid="activity-feed">
            <div className="flex items-center gap-2 mb-4">
              <Activity className={`w-5 h-5 ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`} />
              <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} style={{ fontFamily: 'Unbounded, sans-serif' }}>
                Activity Feed
              </h3>
            </div>
            <div className="space-y-3 max-h-[200px] overflow-y-auto">
              {activityFeed.length === 0 ? (
                <div className="text-center text-gray-500 py-4">No recent activity</div>
              ) : (
                activityFeed.slice(0, 8).map((activity, idx) => (
                  <div key={idx} className={`flex items-start gap-3 p-2 rounded ${isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50'}`}>
                    <div className="mt-0.5">{getActivityIcon(activity.type)}</div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'} truncate`}>
                        {activity.message}
                      </p>
                      <p className="text-xs text-gray-500">
                        {activity.user_name} · {activity.relative_time}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Pipeline Velocity Metrics */}
        <div className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-white border-gray-200'} border rounded-sm p-6`} data-testid="pipeline-velocity">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Clock className={`w-6 h-6 ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`} />
              <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} style={{ fontFamily: 'Unbounded, sans-serif' }}>
                Pipeline Velocity
              </h3>
            </div>
            <select
              value={analyticsPeriod}
              onChange={(e) => setAnalyticsPeriod(e.target.value)}
              className={`${isDark ? 'bg-black border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border rounded-sm px-3 py-1 text-sm focus:border-[#A2FF00]`}
            >
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="year">This Year</option>
              <option value="all">All Time</option>
            </select>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className={`${isDark ? 'bg-white/5' : 'bg-gray-50'} rounded-sm p-4 text-center`}>
              <div className={`text-2xl font-bold ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`} style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {pipelineVelocity.avg_days_to_close}
              </div>
              <div className="text-xs text-gray-500 mt-1">Avg Days to Close</div>
            </div>
            <div className={`${isDark ? 'bg-white/5' : 'bg-gray-50'} rounded-sm p-4 text-center`}>
              <div className="text-2xl font-bold text-[#00D9FF]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {pipelineVelocity.win_rate}%
              </div>
              <div className="text-xs text-gray-500 mt-1">Win Rate</div>
            </div>
            <div className={`${isDark ? 'bg-white/5' : 'bg-gray-50'} rounded-sm p-4 text-center`}>
              <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {pipelineVelocity.avg_pipeline_age}
              </div>
              <div className="text-xs text-gray-500 mt-1">Avg Pipeline Age (days)</div>
            </div>
            <div className={`${isDark ? 'bg-white/5' : 'bg-gray-50'} rounded-sm p-4 text-center`}>
              <div className={`text-2xl font-bold ${pipelineVelocity.stuck_deals_count > 0 ? 'text-orange-400' : isDark ? 'text-white' : 'text-gray-900'}`} style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {pipelineVelocity.stuck_deals_count}
              </div>
              <div className="text-xs text-gray-500 mt-1">Stuck Deals (&gt;14d)</div>
            </div>
            <div className={`${isDark ? 'bg-white/5' : 'bg-gray-50'} rounded-sm p-4 text-center`}>
              <div className={`text-2xl font-bold ${pipelineVelocity.value_at_risk > 0 ? 'text-red-400' : isDark ? 'text-white' : 'text-gray-900'}`} style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                £{pipelineVelocity.value_at_risk.toLocaleString()}
              </div>
              <div className="text-xs text-gray-500 mt-1">Value at Risk</div>
            </div>
          </div>
        </div>

        {/* Total Pipeline + Metrics Cards Row */}
        <div className={`bg-gradient-to-r ${isDark ? 'from-[#A2FF00]/10 to-[#00D9FF]/10 border-[#A2FF00]' : 'from-green-50 to-cyan-50 border-green-500'} border-2 rounded-sm p-6`} data-testid="pipeline-highlight">
          <div className="flex items-center gap-3 mb-4">
            <Target className={`w-8 h-8 ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`} />
            <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} style={{ fontFamily: 'Unbounded, sans-serif' }}>
              Total Pipeline
            </h2>
            <span className={`text-xs ${isDark ? 'bg-[#A2FF00]/20 text-[#A2FF00]' : 'bg-green-100 text-green-700'} px-2 py-1 rounded font-mono`}>
              ACTIVE PROPOSALS
            </span>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className={`${isDark ? 'bg-[#0A0A0A]/60' : 'bg-white/60'} rounded-sm p-4`}>
              <div className="text-sm text-gray-400 mb-1">Pipeline Total</div>
              <div className={`text-3xl font-bold ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`} style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                £{(pipeline.pipeline_total || 0).toLocaleString()}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {pipeline.proposals_count || 0} active proposal{pipeline.proposals_count !== 1 ? 's' : ''}
              </div>
            </div>
            
            <div className={`${isDark ? 'bg-[#0A0A0A]/60' : 'bg-white/60'} rounded-sm p-4`}>
              <div className="text-sm text-gray-400 mb-1">Avg Deal Size</div>
              <div className="text-3xl font-bold text-[#00D9FF]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                £{pipeline.proposals_count > 0 ? Math.round(pipeline.pipeline_total / pipeline.proposals_count).toLocaleString() : 0}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                per proposal
              </div>
            </div>
            
            <div className={`${isDark ? 'bg-[#0A0A0A]/60' : 'bg-white/60'} rounded-sm p-4`}>
              <div className="text-sm text-gray-400 mb-1">Total Proposals</div>
              <div className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                {(pipeline.proposals_count || 0) + (pipeline.signed_count || 0)}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {pipeline.proposals_count || 0} active, {pipeline.signed_count || 0} won
              </div>
            </div>
          </div>
        </div>

        {/* Existing Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-white border-gray-200'} border rounded-sm p-6 hover:border-[#A2FF00]/50 transition-colors`} data-testid="arr-card">
            <div className="flex items-center justify-between mb-4">
              <TrendingUp className={`w-8 h-8 ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`} />
              <span className="text-xs text-gray-500 font-mono">ANNUAL</span>
            </div>
            <div className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-1`} style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              £{metrics.arr.toLocaleString()}
            </div>
            <div className="text-sm text-gray-400">Annual Recurring Revenue</div>
          </div>

          <div className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-white border-gray-200'} border rounded-sm p-6 hover:border-[#A2FF00]/50 transition-colors`} data-testid="mrr-card">
            <div className="flex items-center justify-between mb-4">
              <DollarSign className={`w-8 h-8 ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`} />
              <span className="text-xs text-gray-500 font-mono">MONTHLY</span>
            </div>
            <div className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-1`} style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              £{metrics.mrr.toLocaleString()}
            </div>
            <div className="text-sm text-gray-400">Monthly Recurring Revenue</div>
          </div>

          <div className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-white border-gray-200'} border rounded-sm p-6 hover:border-[#A2FF00]/50 transition-colors`} data-testid="active-contracts-card">
            <div className="flex items-center justify-between mb-4">
              <Users className={`w-8 h-8 ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`} />
              <span className="text-xs text-gray-500 font-mono">ACTIVE</span>
            </div>
            <div className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-1`} style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              {metrics.active_contracts}
            </div>
            <div className="text-sm text-gray-400">Active Contracts</div>
          </div>

          <div className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-white border-gray-200'} border rounded-sm p-6 hover:border-[#A2FF00]/50 transition-colors`} data-testid="renewals-card">
            <div className="flex items-center justify-between mb-4">
              <Calendar className={`w-8 h-8 ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`} />
              <span className="text-xs text-gray-500 font-mono">UPCOMING</span>
            </div>
            <div className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-1`} style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              {metrics.next_month_renewals}
            </div>
            <div className="text-sm text-gray-400">Next Month Renewals</div>
          </div>
        </div>

        {/* Analytics Section: Industry Heatmap + Product Mix + Win/Loss */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Industry Performance */}
          <div className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-white border-gray-200'} border rounded-sm p-6`} data-testid="industry-heatmap">
            <div className="flex items-center gap-2 mb-4">
              <Building2 className={`w-5 h-5 ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`} />
              <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} style={{ fontFamily: 'Unbounded, sans-serif' }}>
                Revenue by Industry
              </h3>
            </div>
            {industries.length === 0 ? (
              <div className="text-center text-gray-500 py-8">No industry data available</div>
            ) : (
              <div className="space-y-3">
                {industries.slice(0, 6).map((ind, idx) => {
                  const maxRevenue = Math.max(...industries.map(i => i.revenue));
                  const percentage = maxRevenue > 0 ? (ind.revenue / maxRevenue) * 100 : 0;
                  return (
                    <div key={idx}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className={`${isDark ? 'text-gray-300' : 'text-gray-700'} truncate mr-2`}>{ind.industry}</span>
                        <span className={`${isDark ? 'text-[#A2FF00]' : 'text-green-600'} font-mono`}>£{ind.revenue.toLocaleString()}</span>
                      </div>
                      <div className={`h-2 ${isDark ? 'bg-white/10' : 'bg-gray-200'} rounded-full overflow-hidden`}>
                        <div 
                          className="h-full rounded-full"
                          style={{ 
                            width: `${percentage}%`,
                            backgroundColor: COLORS[idx % COLORS.length]
                          }}
                        />
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5">{ind.count} deal{ind.count !== 1 ? 's' : ''}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Product Performance */}
          <div className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-white border-gray-200'} border rounded-sm p-6`} data-testid="product-mix">
            <div className="flex items-center gap-2 mb-4">
              <Package className={`w-5 h-5 ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`} />
              <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} style={{ fontFamily: 'Unbounded, sans-serif' }}>
                Product Performance
              </h3>
            </div>
            {products.length === 0 ? (
              <div className="text-center text-gray-500 py-8">No product data available</div>
            ) : (
              <div className="space-y-3">
                {products.slice(0, 5).map((prod, idx) => (
                  <div key={idx} className={`p-3 rounded ${isDark ? 'bg-white/5' : 'bg-gray-50'}`}>
                    <div className="flex justify-between items-start mb-2">
                      <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'} truncate mr-2`}>{prod.name}</span>
                      <span className={`text-sm font-mono ${isDark ? 'text-[#A2FF00]' : 'text-green-600'}`}>£{prod.revenue.toLocaleString()}</span>
                    </div>
                    <div className="flex gap-4 text-xs text-gray-500">
                      <span>{prod.deals_won} won</span>
                      <span>{prod.win_rate}% win rate</span>
                      <span>{prod.days_to_close}d avg close</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Win/Loss Analysis */}
          <div className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-white border-gray-200'} border rounded-sm p-6`} data-testid="loss-reasons">
            <div className="flex items-center gap-2 mb-4">
              <XCircle className="w-5 h-5 text-red-400" />
              <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} style={{ fontFamily: 'Unbounded, sans-serif' }}>
                Loss Reasons
              </h3>
            </div>
            {lossReasons.length === 0 ? (
              <div className="text-center text-gray-500 py-8">No loss data available</div>
            ) : (
              <>
                <ResponsiveContainer width="100%" height={150}>
                  <PieChart>
                    <Pie
                      data={lossReasons}
                      cx="50%"
                      cy="50%"
                      innerRadius={30}
                      outerRadius={60}
                      paddingAngle={2}
                      dataKey="count"
                      nameKey="reason"
                    >
                      {lossReasons.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={['#EF4444', '#F97316', '#FBBF24', '#8B5CF6', '#6B7280'][index % 5]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: isDark ? '#0A0A0A' : '#FFFFFF', 
                        border: `1px solid ${isDark ? '#333' : '#E5E7EB'}`,
                        borderRadius: '4px',
                        color: isDark ? '#FFFFFF' : '#111827'
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-2 mt-2">
                  {lossReasons.slice(0, 4).map((reason, idx) => (
                    <div key={idx} className="flex justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: ['#EF4444', '#F97316', '#FBBF24', '#8B5CF6', '#6B7280'][idx % 5] }} />
                        <span className={isDark ? 'text-gray-300' : 'text-gray-700'}>{reason.reason}</span>
                      </div>
                      <span className="text-gray-500">{reason.percentage}%</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Charts Section */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Pipeline Breakdown Chart */}
          <div className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-white border-gray-200'} border rounded-sm p-6`} data-testid="pipeline-chart">
            <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-6`} style={{ fontFamily: 'Unbounded, sans-serif' }}>
              Pipeline Breakdown
            </h3>
            {pipelineBreakdownData.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={pipelineBreakdownData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                    label={({ name, value }) => `£${value.toLocaleString()}`}
                    labelLine={false}
                  >
                    {pipelineBreakdownData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: isDark ? '#0A0A0A' : '#FFFFFF', 
                      border: `1px solid ${isDark ? '#333' : '#E5E7EB'}`,
                      borderRadius: '4px',
                      color: isDark ? '#FFFFFF' : '#111827'
                    }}
                    formatter={(value, name) => [`£${value.toLocaleString()}`, name]}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-[250px] text-gray-500">
                No active proposals in pipeline
              </div>
            )}
            
            {/* Pipeline Proposals List */}
            {pipeline.proposals_in_pipeline && pipeline.proposals_in_pipeline.length > 0 && (
              <div className="mt-4 space-y-2 max-h-[150px] overflow-y-auto">
                <div className="text-xs text-gray-500 font-medium mb-2">Proposals in Pipeline:</div>
                {pipeline.proposals_in_pipeline.map((proposal) => (
                  <div key={proposal.proposal_id} className={`flex justify-between items-center text-sm ${isDark ? 'bg-white/5' : 'bg-gray-100'} p-2 rounded`}>
                    <span className={`${isDark ? 'text-gray-300' : 'text-gray-700'} truncate mr-2`}>{proposal.title}</span>
                    <div className="flex items-center gap-2 whitespace-nowrap">
                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                        proposal.deal_probability >= 75 ? 'bg-green-500/20 text-green-400' :
                        proposal.deal_probability >= 50 ? 'bg-yellow-500/20 text-yellow-400' :
                        'bg-[#00D9FF]/10 text-[#00D9FF]'
                      }`}>
                        {proposal.deal_probability}%
                      </span>
                      <span className={`${isDark ? 'text-[#A2FF00]' : 'text-green-600'} font-mono`}>
                        £{(proposal.total_gbp || 0).toLocaleString()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Revenue Timeline Chart */}
          <div className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-white border-gray-200'} border rounded-sm p-6 lg:col-span-2`} data-testid="revenue-chart">
            <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-6`} style={{ fontFamily: 'Unbounded, sans-serif' }}>
              Revenue Timeline (12 Months)
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#333' : '#E5E7EB'} />
                <XAxis dataKey="month" stroke={isDark ? '#888' : '#6B7280'} />
                <YAxis stroke={isDark ? '#888' : '#6B7280'} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: isDark ? '#0A0A0A' : '#FFFFFF', 
                    border: `1px solid ${isDark ? '#333' : '#E5E7EB'}`,
                    borderRadius: '4px',
                    color: isDark ? '#FFFFFF' : '#111827'
                  }}
                />
                <Legend />
                <Line type="monotone" dataKey="Revenue" stroke={isDark ? '#A2FF00' : '#16A34A'} strokeWidth={2} name="Revenue (£)" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Monthly Renewals Chart */}
        <div className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-white border-gray-200'} border rounded-sm p-6`} data-testid="renewals-chart">
          <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-6`} style={{ fontFamily: 'Unbounded, sans-serif' }}>
            Monthly Renewals
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#333' : '#E5E7EB'} />
              <XAxis dataKey="month" stroke={isDark ? '#888' : '#6B7280'} />
              <YAxis stroke={isDark ? '#888' : '#6B7280'} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: isDark ? '#0A0A0A' : '#FFFFFF', 
                  border: `1px solid ${isDark ? '#333' : '#E5E7EB'}`,
                  borderRadius: '4px',
                  color: isDark ? '#FFFFFF' : '#111827'
                }}
              />
              <Legend />
              <Bar dataKey="Renewals" fill={isDark ? '#A2FF00' : '#16A34A'} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Retirement Reasons Chart */}
        <div className={`${isDark ? 'bg-[#0A0A0A] border-white/10' : 'bg-white border-gray-200'} border rounded-sm p-6`} data-testid="retirement-chart">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Archive className="w-6 h-6 text-orange-400" />
              <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} style={{ fontFamily: 'Unbounded, sans-serif' }}>
                Retirement Reasons
              </h3>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-400">Period:</span>
              <select
                value={retirementDays}
                onChange={(e) => setRetirementDays(Number(e.target.value))}
                className={`${isDark ? 'bg-black border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'} border rounded-sm px-3 py-1 text-sm focus:border-[#A2FF00]`}
              >
                <option value={30}>Last 30 days</option>
                <option value={90}>Last 90 days</option>
                <option value={180}>Last 180 days</option>
                <option value={365}>Last year</option>
              </select>
            </div>
          </div>
          
          <div className="flex items-center gap-2 mb-4">
            <span className="text-2xl font-bold text-orange-400">{retirementStats.total_retired}</span>
            <span className="text-gray-400">cards retired in the last {retirementDays} days</span>
          </div>

          {retirementStats.data.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              No retired cards in this period
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={retirementStats.data} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#333' : '#E5E7EB'} />
                <XAxis type="number" stroke={isDark ? '#888' : '#6B7280'} />
                <YAxis dataKey="reason" type="category" stroke={isDark ? '#888' : '#6B7280'} width={120} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: isDark ? '#0A0A0A' : '#FFFFFF', 
                    border: `1px solid ${isDark ? '#333' : '#E5E7EB'}`,
                    borderRadius: '4px',
                    color: isDark ? '#FFFFFF' : '#111827'
                  }}
                  formatter={(value) => [`${value} cards`, 'Count']}
                />
                <Bar dataKey="count" fill="#F97316" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </AppLayout>
  );
};

export default Dashboard;
